/**
 * Ellipses Menu Dropdown Component
 * Displays application states and job actions in a dropdown menu
 */

import React from 'react';
import { useIntl } from 'react-intl';
import { APPLICATION_STATES } from '../../../../../components/application-state';
import { Icon } from '../../../../../components/icons/Icon';
import type { ApplicationState } from '../../../../../redux/features/jobs/jobsSlice';

export interface EllipsesMenuDropdownProps {
  isOpen: boolean;
  currentApplicationState: ApplicationState | null;
  isApplied: boolean;
  isHidden: boolean;
  onSetApplicationState: (state: ApplicationState | null) => void;
  onToggleApplied: () => void;
  onToggleHide: () => void;
  onClose: () => void;
}

export const EllipsesMenuDropdown: React.FC<EllipsesMenuDropdownProps> = ({
  isOpen,
  currentApplicationState,
  isApplied,
  isHidden,
  onSetApplicationState,
  onToggleApplied,
  onToggleHide,
  onClose,
}) => {
  const intl = useIntl();

  if (!isOpen) return null;

  return (
    <div className="absolute right-0 top-full mt-2 w-48 rounded-xl shadow bg-white/95 dark:bg-gray-800/95 border border-gray-200 dark:border-gray-700 z-[9999] overflow-hidden">
      {/* Mutually exclusive application states */}
      {APPLICATION_STATES.map((stateConfig) => {
        const isActive = currentApplicationState === stateConfig.value;
        const iconName = isActive ? stateConfig.iconSolid : stateConfig.icon;
        const iconVariant = isActive ? 'solid' : 'outline';
        const colorClasses = {
          purple: isActive ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : 'text-gray-700 dark:text-gray-300',
          green: isActive ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300',
          red: isActive ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'text-gray-700 dark:text-gray-300',
          emerald: isActive ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : 'text-gray-700 dark:text-gray-300',
        };
        return (
          <button
            key={stateConfig.value || 'none'}
            className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
              colorClasses[stateConfig.color as keyof typeof colorClasses]
            }`}
            onClick={() => {
              if (isActive) {
                onSetApplicationState(null);
              } else {
                onSetApplicationState(stateConfig.value);
              }
              onClose();
            }}
          >
            <Icon name={iconName} variant={iconVariant} useMaskForColor className={`h-5 w-5 ${isActive ? '' : 'opacity-60'}`} />
            <span className="flex-1">
              {isActive ? (
                <span className="font-medium">
                  {intl.formatMessage({ id: stateConfig.labelKey, defaultMessage: stateConfig.defaultLabel })} ✓
                </span>
              ) : (
                intl.formatMessage(
                  { id: 'hit.markAsState', defaultMessage: 'Mark as {state}', description: 'Menu item label template for marking job with a specific application state, where {state} is the state name (e.g., Applied, Interviewing)' },
                  { state: intl.formatMessage({ id: stateConfig.labelKey, defaultMessage: stateConfig.defaultLabel }) }
                )
              )}
            </span>
          </button>
        );
      })}

      {/* Separator */}
      <div className="border-t border-gray-200 dark:border-gray-700 my-1" />

      {/* Independent flags (can coexist with mutually exclusive states) */}
      {/* Applied flag */}
      <button
        className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          isApplied ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'
        }`}
        onClick={() => {
          onToggleApplied();
          onClose();
        }}
      >
        <Icon name="checkCircle" variant={isApplied ? 'solid' : 'outline'} useMaskForColor className={`h-5 w-5 ${isApplied ? '' : 'opacity-60'}`} />
        <span className="flex-1">
          {isApplied
            ? intl.formatMessage({ id: 'hit.removeApplied', defaultMessage: 'Remove from applied' })
            : intl.formatMessage({ id: 'hit.markAsApplied.menu', defaultMessage: 'Mark as Applied', description: 'Menu item label to mark job as applied' })}
        </span>
      </button>

      {/* Hidden flag */}
      <button
        className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          isHidden ? 'bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300' : 'text-gray-700 dark:text-gray-300'
        }`}
        onClick={() => {
          onToggleHide();
          onClose();
        }}
      >
        <Icon name="eyeSlash" variant="outline" useMaskForColor className={`h-5 w-5 ${isHidden ? '' : 'opacity-60'}`} />
        <span className="flex-1">
          {isHidden
            ? intl.formatMessage({ id: 'hit.unhide', defaultMessage: 'Unhide job', description: 'Menu item label to unhide a previously hidden job' })
            : intl.formatMessage({ id: 'hit.hide.menu', defaultMessage: 'Hide', description: 'Menu item label to hide a job' })}
        </span>
      </button>
    </div>
  );
};
